import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { auth } from './api/client';
import LandingPage from './pages/LandingPage';
import FAQPage from './pages/FAQPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import UploadPage from './pages/UploadPage';
import DatasetsPage from './pages/DatasetsPage';
import SessionSetupPage from './pages/SessionSetupPage';
import SessionsPage from './pages/SessionsPage';
import ReplayPage from './pages/ReplayPage';
import JournalPage from './pages/JournalPage';
import TradeDetailPage from './pages/TradeDetailPage';
import AnalyticsPage from './pages/AnalyticsPage';
import PopoutChartPage from './pages/PopoutChartPage';
import MarketDataPage from './pages/MarketDataPage';
import NotebookPage from './pages/NotebookPage';
import PlaybookPage from './pages/PlaybookPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import FeedbackButton from './components/FeedbackButton';
import './App.css';

function ProtectedRoute({ user, children }) {
  return user ? children : <Navigate to="/login" />;
}

const PAGE_TITLES = {
  '/login': 'Connexion',
  '/register': 'Inscription',
  '/dashboard': 'Datasets',
  '/upload': 'Import',
  '/sessions': 'Sessions',
  '/journal': 'Journal',
  '/analytics': 'Analytics',
  '/notebook': 'Notebook',
  '/playbooks': 'Playbooks',
  '/market-data': 'Market Data',
  '/admin-panel': 'Admin Dashboard',
};

function PageTitle() {
  const location = useLocation();
  const path = location.pathname;
  const title = PAGE_TITLES[path]
    || (path.startsWith('/replay/') ? 'Replay'
    : path.startsWith('/session/new/') ? 'New Session'
    : path.startsWith('/journal/trade/') ? 'Trade Detail'
    : null);

  if (!title) return null;
  return <Helmet><title>{title} — Tradrin</title></Helmet>;
}

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Datasets', icon: 'M4 6h16M4 12h8m-8 6h16' },
  { path: '/sessions', label: 'Sessions', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { path: '/journal', label: 'Journal', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/analytics', label: 'Analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { path: '/notebook', label: 'Notebook', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
  { path: '/playbooks', label: 'Playbooks', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
  { path: '/market-data', label: 'Market Data', icon: 'M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z' },
  { path: '/upload', label: 'Import', icon: 'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' },
];

function NavIcon({ d }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

function Sidebar({ user, onLogout, collapsed, onToggle, theme, onThemeToggle }) {
  const location = useLocation();

  return (
    <aside className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo">
          {collapsed ? 'T' : 'TradeSimul'}
        </Link>
        <button className="sidebar-toggle" onClick={onToggle}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d={collapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7M19 19l-7-7 7-7'} />
          </svg>
        </button>
      </div>

      <nav className="sidebar-nav">
        {NAV_ITEMS.map(item => {
          const isActive = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <Link key={item.path} to={item.path}
              className={`sidebar-link ${isActive ? 'active' : ''}`}
              title={collapsed ? item.label : ''}>
              <NavIcon d={item.icon} />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          );
        })}
        {user.is_staff && (
          <>
            <div className="sidebar-separator" />
            <Link to="/admin-panel"
              className={`sidebar-link sidebar-link-admin ${location.pathname === '/admin-panel' ? 'active' : ''}`}
              title={collapsed ? 'Admin' : ''}>
              <NavIcon d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              {!collapsed && <span>Admin</span>}
            </Link>
          </>
        )}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-theme-btn" onClick={onThemeToggle} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {theme === 'dark' ? (
              <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            ) : (
              <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
            )}
          </svg>
          {!collapsed && <span>{theme === 'dark' ? 'Light' : 'Dark'}</span>}
        </button>
        <div className="sidebar-user">
          <div className="sidebar-avatar">{user.username.charAt(0).toUpperCase()}</div>
          {!collapsed && (
            <div className="sidebar-user-info">
              <span className="sidebar-username">{user.username}</span>
              <button className="sidebar-logout" onClick={onLogout}>Logout</button>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('tradrin-theme') || 'light');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('tradrin-theme', theme);
  }, [theme]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      auth.me()
        .then((res) => setUser(res.data))
        .catch(() => localStorage.removeItem('token'))
        .finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const handleLogin = (userData) => setUser(userData);
  const handleLogout = async () => {
    try { await auth.logout(); } catch {}
    localStorage.removeItem('token');
    setUser(null);
  };

  if (checking) return <div className="loading">Loading...</div>;

  if (window.location.pathname === '/popout') {
    return (
      <BrowserRouter>
        <Routes><Route path="/popout" element={<PopoutChartPage />} /></Routes>
      </BrowserRouter>
    );
  }

  return (
    <HelmetProvider>
    <BrowserRouter>
      <PageTitle />
      <div className="app-layout">
        {user && (
          <Sidebar user={user} onLogout={handleLogout}
            collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)}
            theme={theme} onThemeToggle={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} />
        )}
        <main className={`app-main ${user ? '' : 'app-main-full'}`}>
          <Routes>
            {/* Public pages */}
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage onLogin={handleLogin} />} />
            <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage onLogin={handleLogin} />} />
            {/* Landing or Dashboard */}
            <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
            {/* Protected app pages */}
            <Route path="/dashboard" element={<ProtectedRoute user={user}><DatasetsPage /></ProtectedRoute>} />
            <Route path="/upload" element={<ProtectedRoute user={user}><UploadPage /></ProtectedRoute>} />
            <Route path="/sessions" element={<ProtectedRoute user={user}><SessionsPage /></ProtectedRoute>} />
            <Route path="/session/new/:datasetId" element={<ProtectedRoute user={user}><SessionSetupPage /></ProtectedRoute>} />
            <Route path="/replay/:sessionId" element={<ProtectedRoute user={user}><ReplayPage /></ProtectedRoute>} />
            <Route path="/journal" element={<ProtectedRoute user={user}><JournalPage /></ProtectedRoute>} />
            <Route path="/journal/trade/:sessionId/:tradeId" element={<ProtectedRoute user={user}><TradeDetailPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute user={user}><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/notebook" element={<ProtectedRoute user={user}><NotebookPage /></ProtectedRoute>} />
            <Route path="/playbooks" element={<ProtectedRoute user={user}><PlaybookPage /></ProtectedRoute>} />
            <Route path="/market-data" element={<ProtectedRoute user={user}><MarketDataPage /></ProtectedRoute>} />
            <Route path="/admin-panel" element={<ProtectedRoute user={user}><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/popout" element={<PopoutChartPage />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
    <FeedbackButton />
    </HelmetProvider>
  );
}

export default App;
